import React, { useState, useEffect } from 'react'
import { CreditCard, DollarSign, Lock, Eye, EyeOff, Save, AlertCircle } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { api } from '../lib/api'

interface PaymentConfig {
  id?: string
  restaurantId: string
  paymentMethod: 'STRIPE' | 'SATISPAY' | 'CASH'
  enabled: boolean
  testMode: boolean
  isConfigured?: boolean
  hasApiKey?: boolean
  hasPublishableKey?: boolean
  hasWebhookSecret?: boolean
}

interface PaymentFormData {
  apiKey: string
  publishableKey: string
  webhookSecret: string
  enabled: boolean
  testMode: boolean
}

export const PaymentSettingsPage: React.FC = () => {
  const [configs, setConfigs] = useState<PaymentConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Form states for each payment method
  const [stripeForm, setStripeForm] = useState<PaymentFormData>({
    apiKey: '',
    publishableKey: '',
    webhookSecret: '',
    enabled: false,
    testMode: true
  })

  const [satispayForm, setSatispayForm] = useState<PaymentFormData>({
    apiKey: '',
    publishableKey: '',
    webhookSecret: '',
    enabled: false,
    testMode: true
  })

  const [showStripeKeys, setShowStripeKeys] = useState(false)
  const [showSatispayKeys, setShowSatispayKeys] = useState(false)

  const { user } = useAuthStore()
  const restaurantId = user?.restaurantId || ''

  useEffect(() => {
    loadConfigurations()
  }, [])

  const loadConfigurations = async () => {
    try {
      setLoading(true)
      const response = await api.get<PaymentConfig[]>(`/api/restaurants/${restaurantId}/payment-configurations`)
      setConfigs(response.data)

      // Initialize form states with existing config
      const stripeConfig = response.data.find(c => c.paymentMethod === 'STRIPE')
      if (stripeConfig) {
        setStripeForm(prev => ({
          ...prev,
          enabled: stripeConfig.enabled,
          testMode: stripeConfig.testMode
        }))
      }

      const satispayConfig = response.data.find(c => c.paymentMethod === 'SATISPAY')
      if (satispayConfig) {
        setSatispayForm(prev => ({
          ...prev,
          enabled: satispayConfig.enabled,
          testMode: satispayConfig.testMode
        }))
      }
    } catch (err) {
      setError('Errore nel caricamento delle configurazioni')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const saveConfiguration = async (paymentMethod: 'STRIPE' | 'SATISPAY', formData: PaymentFormData) => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      // Validate Stripe keys format on frontend
      if (paymentMethod === 'STRIPE') {
        if (formData.apiKey && !formData.apiKey.startsWith('sk_test_') && !formData.apiKey.startsWith('sk_live_')) {
          setError('La chiave API Secret deve iniziare con "sk_test_" o "sk_live_"')
          setSaving(false)
          return
        }
        if (formData.publishableKey && !formData.publishableKey.startsWith('pk_test_') && !formData.publishableKey.startsWith('pk_live_')) {
          setError('La chiave Publishable deve iniziare con "pk_test_" o "pk_live_"')
          setSaving(false)
          return
        }
      }

      await api.put(`/api/restaurants/${restaurantId}/payment-configurations`, {
        paymentMethod,
        apiKey: formData.apiKey || undefined,
        publishableKey: formData.publishableKey || undefined,
        webhookSecret: formData.webhookSecret || undefined,
        enabled: formData.enabled,
        testMode: formData.testMode
      })

      setSuccess(`Configurazione ${paymentMethod} salvata con successo!`)
      await loadConfigurations()

      // Clear form fields after save
      if (paymentMethod === 'STRIPE') {
        setStripeForm(prev => ({ ...prev, apiKey: '', publishableKey: '', webhookSecret: '' }))
      } else {
        setSatispayForm(prev => ({ ...prev, apiKey: '', publishableKey: '', webhookSecret: '' }))
      }

      setTimeout(() => setSuccess(null), 3000)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || `Errore nel salvare la configurazione ${paymentMethod}`
      setError(errorMessage)
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const getConfigStatus = (paymentMethod: 'STRIPE' | 'SATISPAY' | 'CASH') => {
    const config = configs.find(c => c.paymentMethod === paymentMethod)
    return config
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center dark:bg-[#1a1a1f]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-white/60">Caricamento configurazioni...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1f] py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Configurazione Metodi di Pagamento
          </h1>
          <p className="text-gray-600 dark:text-white/60">
            Configura i metodi di pagamento disponibili per i tuoi clienti
          </p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 dark:text-red-400">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-green-800 dark:text-green-400">{success}</p>
          </div>
        )}

        {/* Cash Payment (Always Available) */}
        <div className="bg-white dark:bg-[#25252d] rounded-xl shadow-sm border border-gray-200 dark:border-white/[0.08] p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-green-100 dark:bg-green-500/20 p-3 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Contanti</h3>
                <p className="text-sm text-gray-600 dark:text-white/60">Pagamento al ritiro</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                Attivo
              </span>
            </div>
          </div>
        </div>

        {/* Stripe Configuration */}
        <div className="bg-white dark:bg-[#25252d] rounded-xl shadow-sm border border-gray-200 dark:border-white/[0.08] p-6 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-purple-100 dark:bg-purple-500/20 p-3 rounded-lg">
              <CreditCard className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Stripe</h3>
              <p className="text-sm text-gray-600 dark:text-white/60">Carte di credito e debito</p>
            </div>
            {getConfigStatus('STRIPE')?.isConfigured && (
              <span className="px-3 py-1 bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                Configurato
              </span>
            )}
          </div>

          {/* Info Box - How to get Stripe keys */}
          <div className="mb-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <strong>Come ottenere le chiavi Stripe:</strong>
              <br />
              1. Vai su <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className="underline">stripe.com</a> e crea un account gratuito
              <br />
              2. Dashboard → Developers → API keys
              <br />
              3. Copia la <strong>Secret key</strong> (inizia con sk_test_...) e la <strong>Publishable key</strong> (inizia con pk_test_...)
            </p>
          </div>

          {/* Stripe Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                API Secret Key <span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <div className="relative">
                <input
                  type={showStripeKeys ? 'text' : 'password'}
                  value={stripeForm.apiKey}
                  onChange={(e) => setStripeForm({ ...stripeForm, apiKey: e.target.value })}
                  placeholder={getConfigStatus('STRIPE')?.hasApiKey ? '••••••••••••' : 'sk_test_... o sk_live_...'}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/[0.12] rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-10 bg-white dark:bg-white/[0.04] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30"
                />
                <button
                  type="button"
                  onClick={() => setShowStripeKeys(!showStripeKeys)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  {showStripeKeys ? <EyeOff className="w-5 h-5 text-gray-400 dark:text-white/40" /> : <Eye className="w-5 h-5 text-gray-400 dark:text-white/40" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-white/40">Deve iniziare con sk_test_ o sk_live_</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                Publishable Key <span className="text-red-600 dark:text-red-400">*</span>
              </label>
              <input
                type="text"
                value={stripeForm.publishableKey}
                onChange={(e) => setStripeForm({ ...stripeForm, publishableKey: e.target.value })}
                placeholder={getConfigStatus('STRIPE')?.hasPublishableKey ? '••••••••••••' : 'pk_test_... o pk_live_...'}
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/[0.12] rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-white/[0.04] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-white/40">Deve iniziare con pk_test_ o pk_live_</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                Webhook Secret <span className="text-gray-500 dark:text-white/40 text-sm">(opzionale)</span>
              </label>
              <input
                type={showStripeKeys ? 'text' : 'password'}
                value={stripeForm.webhookSecret}
                onChange={(e) => setStripeForm({ ...stripeForm, webhookSecret: e.target.value })}
                placeholder={getConfigStatus('STRIPE')?.hasWebhookSecret ? '••••••••••••' : 'whsec_... (opzionale per test)'}
                className="w-full px-4 py-2 border border-gray-300 dark:border-white/[0.12] rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-white/[0.04] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-white/40">Necessario solo se utilizzi webhook Stripe (non richiesto per pagamenti base)</p>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={stripeForm.enabled}
                  onChange={(e) => setStripeForm({ ...stripeForm, enabled: e.target.checked })}
                  className="w-4 h-4 text-purple-600 border-gray-300 dark:border-white/20 rounded focus:ring-purple-500 bg-white dark:bg-white/[0.04]"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-white/70">Attivo</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={stripeForm.testMode}
                  onChange={(e) => setStripeForm({ ...stripeForm, testMode: e.target.checked })}
                  className="w-4 h-4 text-purple-600 border-gray-300 dark:border-white/20 rounded focus:ring-purple-500 bg-white dark:bg-white/[0.04]"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-white/70">Modalità Test</span>
              </label>
            </div>

            <button
              onClick={() => saveConfiguration('STRIPE', stripeForm)}
              disabled={saving}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Salvataggio...' : 'Salva Configurazione Stripe'}
            </button>
          </div>
        </div>

        {/* Satispay Configuration */}
        <div className="bg-white dark:bg-[#25252d] rounded-xl shadow-sm border border-gray-200 dark:border-white/[0.08] p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-red-100 dark:bg-red-500/20 p-3 rounded-lg">
              <CreditCard className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Satispay</h3>
              <p className="text-sm text-gray-600 dark:text-white/60">Pagamento digitale italiano</p>
            </div>
            {getConfigStatus('SATISPAY')?.isConfigured && (
              <span className="px-3 py-1 bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 rounded-full text-sm font-medium">
                Configurato
              </span>
            )}
          </div>

          {/* Satispay Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-white/70 mb-2">
                API Key
              </label>
              <div className="relative">
                <input
                  type={showSatispayKeys ? 'text' : 'password'}
                  value={satispayForm.apiKey}
                  onChange={(e) => setSatispayForm({ ...satispayForm, apiKey: e.target.value })}
                  placeholder={getConfigStatus('SATISPAY')?.hasApiKey ? '••••••••••••' : 'Inserisci API Key Satispay'}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/[0.12] rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent pr-10 bg-white dark:bg-white/[0.04] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30"
                />
                <button
                  type="button"
                  onClick={() => setShowSatispayKeys(!showSatispayKeys)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  {showSatispayKeys ? <EyeOff className="w-5 h-5 text-gray-400 dark:text-white/40" /> : <Eye className="w-5 h-5 text-gray-400 dark:text-white/40" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={satispayForm.enabled}
                  onChange={(e) => setSatispayForm({ ...satispayForm, enabled: e.target.checked })}
                  className="w-4 h-4 text-red-600 border-gray-300 dark:border-white/20 rounded focus:ring-red-500 bg-white dark:bg-white/[0.04]"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-white/70">Attivo</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={satispayForm.testMode}
                  onChange={(e) => setSatispayForm({ ...satispayForm, testMode: e.target.checked })}
                  className="w-4 h-4 text-red-600 border-gray-300 dark:border-white/20 rounded focus:ring-red-500 bg-white dark:bg-white/[0.04]"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-white/70">Modalità Test</span>
              </label>
            </div>

            <button
              onClick={() => saveConfiguration('SATISPAY', satispayForm)}
              disabled={saving}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Salvataggio...' : 'Salva Configurazione Satispay'}
            </button>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-300">
              <p className="font-semibold mb-1">Sicurezza</p>
              <p>Le tue credenziali sono archiviate in modo sicuro e crittografato. Non condividere mai le tue chiavi API con nessuno.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
